import { useQueryClient } from "@tanstack/react-query";
import { Article, Artist, Comic, Release, Section } from "../types";
import { useAccountData } from "./useAccountData";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    (value.constructor === Object || value.constructor === undefined)
  );
}

function preserveFuller(oldVal: any, newVal: any): any {
  if (oldVal === undefined) return newVal;
  if (newVal === undefined) return oldVal;
  if (!isPlainObject(oldVal) || !isPlainObject(newVal)) return newVal;

  const merged: any = { ...oldVal, ...newVal };
  for (const key of Object.keys(oldVal)) {
    const o = oldVal[key];
    const n = newVal[key];
    const nIsEmpty =
      n === undefined ||
      n === null ||
      n === "" ||
      (Array.isArray(n) && n.length === 0);
    const oIsPopulated =
      o !== undefined &&
      o !== null &&
      o !== "" &&
      !(Array.isArray(o) && o.length === 0);
    if (nIsEmpty && oIsPopulated) {
      merged[key] = o;
    } else if (isPlainObject(o) && isPlainObject(n)) {
      // Nested entity placeholder check: if the new nested object's id is
      // falsy but the old one has a real id, the new is a placeholder —
      // preserve the entire old subtree instead of recursing (which would
      // accept id=0 and break downstream cache-key lookups).
      const oId = (o as any).id;
      const nId = (n as any).id;
      if (oId && !nId) {
        merged[key] = o;
      } else {
        merged[key] = preserveFuller(o, n);
      }
    }
  }
  return merged;
}

export const useEntityCache = () => {
  const queryClient = useQueryClient();
  const accountData = useAccountData();

  const comicFavorites = accountData?.comic_favorites ?? [];

  const saveComic = (section: Section, comic: Comic) => {
    if (
      !section.meta?.layout.display?.include_add_library_cta &&
      !section.meta?.layout.display?.include_facet
    ) {
      delete comic.library_status;
    }

    if (!section.meta?.layout.display?.include_match) {
      delete comic.comic_facets;
    }

    saveEntityToCache(comic);
  };

  const saveEntityToCache = (n: any) => {
    const cacheKey = `${n.entity_type}-${n.id}`;
    const cacheData = n;

    queryClient.setQueryData(
      [cacheKey],
      (oldCacheData: undefined | Comic | Artist | Release | Article) => {
        if (oldCacheData && oldCacheData.entity_type === "Comic") {
          // Save comic with a library status if it's in the account comic favorites
          if (!oldCacheData.library_status && comicFavorites.includes(n.id)) {
            cacheData.is_favorite = true;

            cacheData.library_status = {
              entity_type: "ComicFavorite",
              comic_id: n.id,
              user_id: accountData?.user?.id ?? 0,
              comic_user_reading_status_id: 1,
              disable_notifications: 0,
              disable_emails: 0,
            };
          }
        } else if (n.entity_type === "Artist") {
          if (accountData?.artist_favorites?.includes(n.id)) {
            cacheData.user_is_following = true;
          }
        }

        // Don't let a partial payload (browse rail / card) clobber a fuller
        // payload (full-detail fetch). For any field where the old value is
        // populated but the new one is empty/missing, keep the old value.
        // For nested objects (e.g. comic.artist), recursively preserve
        // populated old fields so a partial nested object doesn't clobber a
        // fuller one.
        return preserveFuller(oldCacheData, cacheData);
      },
    );
  };

  return {
    saveComic,
    saveEntityToCache,
  };
};
